import { useState } from 'react';
import type { MockViolation } from '../types/mockdetector.types';
import type { SourceFileScanConfig } from '../types/scanConfig.types';
import { MOCK_DATA_PATTERNS, STATIC_DATA_PATTERNS } from '../utils/scanPatterns';
import { scanReactCode } from '../utils/reactPatterns';
import { isWhitelistedFile, isWhitelistedCode } from '../utils/smartWhitelist'; // ✅ NEW: Smart whitelist

/**
 * Client-side source code scanner using Vite's import.meta.glob
 * This scanner works in ANY Lovable project - it reads THEIR source files
 * No backend/Edge Function required - 100% standalone
 */
export const useClientSourceScanner = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filesScanned, setFilesScanned] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  /**
   * Scan all source files in the project using Vite's import.meta.glob
   * This reads the actual project files from /src/**
   */
  const scanProjectFiles = async (_config?: SourceFileScanConfig): Promise<MockViolation[]> => {
    setIsScanning(true);
    setProgress(0);

    const startTime = performance.now();
    let timeoutId: number | null = null;
    let isTimedOut = false;

    try {
      // Use Vite's import.meta.glob to read ALL source files in the project
      // This works for ANY Lovable project - reads THEIR files, not ours
      const modules = import.meta.glob('/src/**/*.{ts,tsx,js,jsx}', { 
        query: '?raw',
        import: 'default',
        eager: false
      });

      const filePaths = Object.keys(modules);
      const totalFilesCount = filePaths.length;
      
      setTotalFiles(totalFilesCount);
      setFilesScanned(0);
      
      // Smart timeout based on project size (safety net for cost protection)
      const timeoutMs = totalFilesCount < 300 ? 180000 :      // Small: 3 minutes
                        totalFilesCount < 1000 ? 300000 :     // Medium: 5 minutes  
                        600000;                               // Large: 10 minutes
      
      console.log(`📁 Client-side scanner found ${totalFilesCount} files to scan`);
      console.log(`⏱️ Timeout set to ${timeoutMs / 1000 / 60} minutes for safety`);
      
      // Set timeout to prevent infinite scanning (cost protection)
      timeoutId = window.setTimeout(() => {
        isTimedOut = true;
        console.warn(`⏱️ Scan timeout after ${timeoutMs / 1000 / 60} minutes - returning partial results`);
      }, timeoutMs);

      const allViolations: MockViolation[] = [];
      let scannedCount = 0;

      // Scan each file (with timeout protection)
      for (const filePath of filePaths) {
        // Check timeout
        if (isTimedOut) {
          console.warn(`⏱️ Scan timed out after scanning ${scannedCount}/${totalFilesCount} files`);
          console.log(`📊 Returning ${allViolations.length} violations from partial scan`);
          break;
        }

        try {
          // Load file content
          const fileContent = await modules[filePath]() as string;
          
          // Scan file for violations
          const violations = scanFileContent(fileContent, filePath);
          allViolations.push(...violations);

          scannedCount++;
          setFilesScanned(scannedCount);
          setProgress((scannedCount / totalFilesCount) * 100);

        } catch (error) {
          console.warn(`⚠️ Could not scan file ${filePath}:`, error);
          scannedCount++; // Count even failed files
          setFilesScanned(scannedCount);
          setProgress((scannedCount / totalFilesCount) * 100);
        }
      }
      
      // Clear timeout
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      const scanDuration = (performance.now() - startTime) / 1000;
      
      if (isTimedOut) {
        console.log(`⚠️ PARTIAL scan complete (timed out): ${allViolations.length} violations found in ${scannedCount}/${totalFilesCount} files (${scanDuration.toFixed(1)}s)`);
      } else {
        console.log(`✅ Client-side scan complete: ${allViolations.length} violations found in ${scannedCount} files (${scanDuration.toFixed(1)}s)`);
      }
      
      setIsScanning(false);
      setProgress(100);
      setFilesScanned(totalFilesCount);
      
      return allViolations;

    } catch (error) {
      console.error('❌ Client-side scanner error:', error);
      setIsScanning(false);
      
      // Clear timeout on error
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      
      return [];
    }
  };

  /**
   * Normalize and validate file paths to ensure correct reporting
   */
  const normalizeFilePath = (rawPath: string): string => {
    // Validate: path should end with .tsx, .ts, .jsx, or .js
    if (!rawPath.endsWith('.tsx') && !rawPath.endsWith('.ts') && 
        !rawPath.endsWith('.jsx') && !rawPath.endsWith('.js')) {
      console.warn(`⚠️ Invalid file path detected: ${rawPath}`);
      return rawPath; // Keep as-is but log warning
    }
    
    // Remove leading slashes and normalize
    return rawPath.replace(/^\/+/, '');
  };

  /**
   * Detect TODO/FIXME comments about mock data
   */
  const detectTodoComments = (content: string, filePath: string): MockViolation[] => {
    const violations: MockViolation[] = [];
    const lines = content.split('\n');
    
    lines.forEach((line, index) => {
      const lineNumber = index + 1;
      const trimmedLine = line.trim();
      
      // Check for TODO/FIXME comments mentioning mock/test/demo data
      if (/\/\/.*(?:TODO|FIXME).*(?:mock|test|demo|sample|fake|hardcoded|placeholder)/gi.test(trimmedLine)) {
        // Extract 5 lines context
        const startLine = Math.max(0, index - 2);
        const endLine = Math.min(lines.length, index + 3);
        const codeSnippet = lines.slice(startLine, endLine).join('\n');
        
        violations.push({
          type: 'mock_data',
          severity: 'medium',
          description: 'TODO/FIXME comment about mock data - needs cleanup',
          code: codeSnippet,
          file: filePath.replace('/src/', 'src/'),
          line: lineNumber,
          fix: 'Address this TODO comment by replacing mock data with real data',
        });
      }
    });
    
    return violations;
  };

  /**
   * ✅ PHASE 1 ENHANCEMENT: Scan a single file's content with smart whitelisting
   */
  const scanFileContent = (content: string, filePath: string): MockViolation[] => {
    const violations: MockViolation[] = [];
    const lines = content.split('\n');
    
    // Normalize path FIRST
    const normalizedPath = normalizeFilePath(filePath);
    
    // ✅ NEW: Skip whitelisted files entirely
    if (isWhitelistedFile(normalizedPath)) {
      console.log(`✓ Skipping whitelisted file: ${normalizedPath}`);
      return [];
    }

    // PASS 1: Apply mock data patterns (with expanded context + whitelisting)
    MOCK_DATA_PATTERNS.forEach(({ pattern, severity, description }) => {
      const matches = content.matchAll(new RegExp(pattern, 'g'));
      
      for (const match of matches) {
        if (!match[0]) continue;

        // Find line number
        const beforeMatch = content.substring(0, match.index || 0);
        const lineNumber = beforeMatch.split('\n').length;
        const matchedLine = lines[lineNumber - 1];
        
        // ✅ NEW: Skip whitelisted code patterns
        if (matchedLine && isWhitelistedCode(matchedLine)) {
          continue;
        }

        // Extract code snippet (10 lines context - 5 before, 5 after)
        const startLine = Math.max(0, lineNumber - 5);
        const endLine = Math.min(lines.length, lineNumber + 5);
        const codeSnippet = lines.slice(startLine, endLine).join('\n');

        violations.push({
          type: 'mock_data',
          severity,
          description,
          code: codeSnippet,
          file: normalizedPath.replace('/src/', 'src/'),
          line: lineNumber,
          fix: `Replace mock data with real data from your database or API`,
        });
      }
    });

    // PASS 2: Apply static data patterns (with whitelisting)
    STATIC_DATA_PATTERNS.forEach(({ pattern, severity, description }) => {
      const matches = content.matchAll(new RegExp(pattern, 'g'));
      
      for (const match of matches) {
        if (!match[0]) continue;

        const beforeMatch = content.substring(0, match.index || 0);
        const lineNumber = beforeMatch.split('\n').length;
        const matchedLine = lines[lineNumber - 1];
        
        // ✅ NEW: Skip whitelisted code patterns
        if (matchedLine && isWhitelistedCode(matchedLine)) {
          continue;
        }

        // Extract code snippet (10 lines context)
        const startLine = Math.max(0, lineNumber - 5);
        const endLine = Math.min(lines.length, lineNumber + 5);
        const codeSnippet = lines.slice(startLine, endLine).join('\n');

        violations.push({
          type: 'mock_data',
          severity,
          description,
          code: codeSnippet,
          file: normalizedPath.replace('/src/', 'src/'),
          line: lineNumber,
          fix: `Replace static array with data fetched from your database`,
        });
      }
    });

    // PASS 3: React-specific patterns (highest quality detection)
    const reactViolations = scanReactCode(content, normalizedPath);
    violations.push(...reactViolations);

    // PASS 4: TODO/FIXME comments about mock data
    const todoViolations = detectTodoComments(content, normalizedPath);
    violations.push(...todoViolations);

    return violations;
  };

  return {
    scanProjectFiles,
    isScanning,
    progress,
    filesScanned,
    totalFiles,
  };
};
